import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment';
import { saveAs } from 'file-saver';
import { pdf } from '@react-pdf/renderer';
import { toast } from 'react-toastify';
import { Context } from 'Context/Context';
import useIsMobile from 'Hooks/useIsMobile';
import { Avatar } from 'Components/Professional/Cases/TeamupUI';
import { CaseSummaryDoc, downloadCaseBundle } from './CaseSummaryPdf';

// Client view of a closed case: what happened, who they closed it with (with
// contact details), when it will be deleted, and a one-click download of the
// whole case (PDF summary + every file they uploaded) before that happens.
const Icon = ({ d, size = 16, stroke = 'currentColor', width = 2 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={width} strokeLinecap="round" strokeLinejoin="round" className='flex-none'>
    {(Array.isArray(d) ? d : [d]).map((p, i) => <path key={i} d={p} />)}
  </svg>
);
const I = {
  check: 'M5 12l5 5 9-10',
  clock: ['M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18z', 'M12 7v5l3 2'],
  download: ['M12 3v12', 'M7 10l5 5 5-5', 'M4 20h16'],
  file: ['M14 3H6v18h12V7z', 'M14 3v4h4'],
  mail: ['M3 5h18v14H3z', 'M3 6l9 7 9-7'],
  phone: 'M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2',
};

function PersonCard({ p, role, note, muted }) {
  if (!p) return null;
  const name = p.isUserFunder && p.company ? p.company : `${p.firstName || ''} ${p.lastName || ''}`.trim();
  const sub = p.isUserFunder ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : p.company;
  const facts = [
    p.legalType && ['Designation', p.legalType],
    p.experience && ['Experience', `${p.experience} years`],
  ].filter(Boolean);
  return (
    <div className={`bg-white border border-navy/[.12] rounded-lg py-[18px] px-5 flex flex-col gap-3.5 min-w-0 ${muted ? 'opacity-[.85]' : 'opacity-100'}`}>
      <span className='font-plexsans font-semibold text-[11px] leading-none tracking-[.08em] uppercase text-navy/[.55]'>{role}</span>
      <div className='flex items-center gap-3.5 min-w-0'>
        <Avatar user={p} size={48} />
        <div className='flex flex-col gap-[3px] min-w-0'>
          <span className='font-spectral font-normal text-[19px] leading-[1.25] text-navy'>{name || 'Details not found'}</span>
          {sub && <span className='font-plexsans font-normal text-[13px] leading-[1.35] text-navy/[.6]'>{sub}</span>}
        </div>
      </div>
      {note && <span className='font-plexsans font-medium text-[12.5px] leading-[1.45] text-brass bg-brass/[.07] border border-brass/[.22] rounded-[5px] py-2 px-2.5'>{note}</span>}
      <div className='flex flex-col gap-[9px]'>
        {p.email && <a href={'mailto:' + p.email} className='flex items-center gap-[9px] font-plexsans font-medium text-[13.5px] leading-[1.3] text-navy no-underline [overflow-wrap:anywhere]'><span className='text-brass flex'><Icon d={I.mail} size={15} /></span>{p.email}</a>}
        {p.phoneNumber && <a href={'tel:' + p.phoneNumber} className='flex items-center gap-[9px] font-plexsans font-medium text-[13.5px] leading-[1.3] text-navy no-underline'><span className='text-brass flex'><Icon d={I.phone} size={15} /></span>{p.phoneNumber}</a>}
      </div>
      {facts.length > 0 &&
        <div className='flex flex-wrap gap-1.5'>
          {facts.map(([k, v]) => <span key={k} className='font-plexsans font-medium text-[12px] leading-none text-navy bg-navy/[.05] border border-navy/[.12] rounded-[5px] py-1.5 px-[9px]'>{k}: {v}</span>)}
        </div>}
    </div>
  );
}

function ClosedCaseStepper({profDetails, caseDetails}) {
  const [closedProfileDetails, setClosedProfileDetails] = useState([]);
  const [teamupToUser, setTeamupToUser] = useState(null);
  const [changedProfile, setChangedProfile] = useState(null);
  const [busy, setBusy] = useState(null);     // progress text while preparing a download
  const {makeApiCallFunc} = useContext(Context);
  const isMobile = useIsMobile();

  useEffect(() => {
    if(profDetails?.userId?.profile)
      {
        setClosedProfileDetails(profDetails?.userId?.profile);
        setChangedProfile(profDetails?.userId?.userId)
      }
      else
      {
        setClosedProfileDetails(profDetails?.userId ? [profDetails?.userId] : []);
      }
      if(profDetails?.teamup?.toUser)
      {
        setTeamupToUser(profDetails?.teamup?.toUser?._id === profDetails?.userId?._id ? profDetails?.teamup?.fromUser : profDetails?.teamup?.toUser);
      }
    },[profDetails]);

  const main = closedProfileDetails.filter((p) => p?._id);
  const lead = main[0] || changedProfile;
  const isFunder = !!lead?.isUserFunder;
  const closedOn = profDetails?.updated_at;
  const deleteOn = closedOn ? moment(closedOn).add(7, 'days') : null;
  const daysLeft = deleteOn ? Math.max(0, Math.ceil(deleteOn.diff(moment(), 'days', true))) : null;
  const roleOf = (p) => (p?.isUserFunder ? 'Litigation funder' : 'Attorney');
  const d = caseDetails || {};
  const fileCount = (d.supportingDocuments?.length || 0) + (d.videoDocument?.length || 0);
  const people = [
    ...main.map((p) => ({ p, role: roleOf(p) + (changedProfile?.email ? ' (details at closing)' : '') })),
    ...(changedProfile?.email ? [{ p: changedProfile, role: roleOf(changedProfile) + ' (current details)' }] : []),
    ...(teamupToUser ? [{ p: teamupToUser, role: 'Teamed up with · ' + roleOf(teamupToUser) }] : []),
  ];

  const downloadAll = async () => {
    if (busy || !caseDetails) return;
    setBusy('Preparing…');
    try {
      const res = await downloadCaseBundle({ caseDetails, people, closedOn, makeApiCallFunc, onProgress: setBusy });
      if (res.failed) toast.warning(`${res.failed} file(s) couldn't be added. Try again, or download them from Provided Case Information.`);
      else toast.success('Your case download is ready.');
    } catch (e) {
      console.log(e);
      toast.error("Couldn't prepare the download. Please try again.");
    }
    setBusy(null);
  };
  const downloadPdf = async () => {
    if (busy || !caseDetails) return;
    setBusy('Preparing summary…');
    try {
      const blob = await pdf(<CaseSummaryDoc caseDetails={caseDetails} people={people} closedOn={closedOn} />).toBlob();
      saveAs(blob, `${d.caseNumber || 'case'} - case summary.pdf`);
    } catch (e) { console.log(e); toast.error("Couldn't prepare the PDF. Please try again."); }
    setBusy(null);
  };

  const teamupLine = teamupToUser && lead
    ? `${lead.isUserFunder ? (lead.company || `${lead.firstName} ${lead.lastName}`) : `${lead.firstName} ${lead.lastName}`} teamed up with ${teamupToUser.isUserFunder ? (teamupToUser.company || `${teamupToUser.firstName} ${teamupToUser.lastName}`) : `${teamupToUser.firstName} ${teamupToUser.lastName}`} to close this case.`
    : null;

  return (
    <div className={`font-plexsans w-full max-w-[980px] mx-auto flex flex-col gap-[18px] ${isMobile ? 'pt-4 pb-2' : 'pt-6 pb-2'}`}>
      {/* hero */}
      <div className='bg-white border border-navy/[.12] rounded-lg overflow-hidden'>
        <div className={`flex items-start gap-4 ${isMobile ? 'py-5 px-[18px]' : 'py-[26px] px-7'}`}>
          <span className={`flex-none rounded-full bg-success/[.1] text-success flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-12 h-12'}`}><Icon d={I.check} size={isMobile ? 20 : 24} width={2.6} /></span>
          <div className='flex flex-col gap-[7px] min-w-0'>
            <span className='font-plexmono font-medium text-[11px] leading-none tracking-[.08em] uppercase text-success'>Case closed{closedOn ? ' · ' + moment(closedOn).format('MMM D, YYYY') : ''}</span>
            <span className={`font-spectral font-normal leading-[1.25] text-navy ${isMobile ? 'text-[20px]' : 'text-[24px]'}`}>
              Congratulations — you've secured {isFunder ? 'a litigation funder' : 'an attorney'} for your case
            </span>
            <span className='font-plexsans font-normal text-[14px] leading-[1.55] text-navy/[.7]'>
              {teamupLine ? teamupLine + ' ' : ''}Their contact details are below. Reach out to them directly for anything about your case from here on.
            </span>
          </div>
        </div>
        {/* keep a copy */}
        <div className={`flex justify-between gap-4 bg-ivory border-t border-navy/[.08] ${isMobile ? 'flex-col items-stretch pt-4 px-[18px] pb-[18px]' : 'flex-row items-center py-[18px] px-7'}`}>
          <div className='flex items-start gap-2.5 min-w-0'>
            <span className='text-brass flex mt-px'><Icon d={I.clock} size={17} /></span>
            <div className='flex flex-col gap-[3px]'>
              <span className='font-plexsans font-semibold text-[13.5px] leading-[1.35] text-navy'>
                {deleteOn ? <>This case will be deleted on {deleteOn.format('MMM D, YYYY')}{daysLeft != null ? ` (${daysLeft === 0 ? 'today' : daysLeft === 1 ? 'in 1 day' : `in ${daysLeft} days`})` : ''}</> : 'Closed cases are deleted after 7 days'}
              </span>
              <span className='font-plexsans font-normal text-[12.5px] leading-[1.45] text-navy/[.6]'>
                Download a copy to keep: a PDF summary of your case{fileCount ? ` and the ${fileCount} file${fileCount === 1 ? '' : 's'} you uploaded` : ''}. You can also delete it sooner from the header.
              </span>
            </div>
          </div>
          <div className='flex gap-2.5 flex-wrap flex-none'>
            <button type="button" onClick={downloadPdf} disabled={!!busy} className={`inline-flex items-center justify-center gap-[7px] font-plexsans font-semibold text-[13px] leading-none text-navy bg-white border border-navy/[.25] rounded-md py-3 px-4 whitespace-nowrap hover:bg-cream ${isMobile ? 'flex-1' : 'flex-none'} ${busy ? 'cursor-default' : 'cursor-pointer'}`}>
              <Icon d={I.file} size={15} />Summary (PDF)
            </button>
            <button type="button" onClick={downloadAll} disabled={!!busy} className={`inline-flex items-center justify-center gap-[7px] font-plexsans font-semibold text-[13px] leading-none text-ivory bg-brass border-0 rounded-md py-3 px-[18px] whitespace-nowrap hover:bg-brassDark transition-colors ${isMobile ? 'flex-1' : 'flex-none'} ${busy ? 'cursor-default opacity-80' : 'cursor-pointer opacity-100'}`}>
              <Icon d={I.download} size={15} />{busy || (fileCount ? 'Download everything (.zip)' : 'Download case (.zip)')}
            </button>
          </div>
        </div>
      </div>

      {/* people */}
      {people.length ?
        <div className={`grid gap-4 ${isMobile || people.length === 1 ? 'grid-cols-[minmax(0,1fr)]' : 'grid-cols-2'}`}>
          {main.map((p) => <PersonCard key={p._id} p={p} role={changedProfile?.email ? `Your ${roleOf(p).toLowerCase()} · at closing` : `Your ${roleOf(p).toLowerCase()}`} muted={!!changedProfile?.email} />)}
          {changedProfile?.email && <PersonCard p={changedProfile} role={`Your ${roleOf(changedProfile).toLowerCase()} · now`} note="They've moved to a new firm since closing. These are their current details." />}
          {teamupToUser && <PersonCard p={teamupToUser} role={`Teamed up with · ${roleOf(teamupToUser).toLowerCase()}`} />}
        </div>
        :
        <div className='bg-white border border-navy/[.12] rounded-lg p-5 font-plexsans font-normal text-[13.5px] leading-[1.5] text-navy/[.6]'>
          We couldn't find the details of who closed this case. Please contact support if you need them.
        </div>}
    </div>
  )
}

export default ClosedCaseStepper
